/**
 * Fi client for trading operations
 * Follows DexClient pattern from pkg/netrunner/clients/dex_client.go
 * Handles EIP-712 signatures and automatic satoshi conversion
 */

import { BaseSignedClient } from "./BaseSignedClient";
import { toSatoshi } from "../../utils/math";
import { fromSatoshi } from "../../utils/math";
import {
  shouldRefreshNonce,
  isDuplicateTransactionError,
} from "../../utils/errorDetection";
import { toRFC3339Nano } from "../../utils/time";
import {
  OpSubmitOrder,
  OpCancelOrder,
  OpModifyOrder,
  OpUpdatePositionLeverage,
  OpClosePosition,
  OpSwap,
  OpChangeMarginRatio,
  OpInternTransfer,
  OpBankBucketTransfer,
  OpBankTransferToBucket,
  OpBankWithdrawFromBucket,
  OpBankMint,
  OpBankTransfer,
  OpCreateMarginBucket,
  ExchangeClientConfig,
  PlaceOrderParams,
  CancelOrderParams,
  ModifyOrderParams,
  ChangeLeverageParams,
  ClosePositionParams,
  SwapParams,
  ChangeMarginRatioParams,
  InternTransferParams,
  BucketTransferParams,
  ExtractToBucketParams,
  WithdrawFromBucketParams,
  BankMintParams,
  BankTransferParams,
  CreateBucketParams,
  BatchOrderParams,
  PlaceOrderResponse,
  CancelOrderResponse,
  ModifyOrderResponse,
  ChangeLeverageResponse,
  ClosePositionResponse,
  ChangeMarginRatioResponse,
  InternTransferResponse,
  BucketTransferResponse,
  ExtractToBucketResponse,
  WithdrawFromBucketResponse,
  BankMintResponse,
  BankTransferResponse,
  CreateBucketResponse,
  BatchOrderResponse,
} from "../../types";

export class FiClient extends BaseSignedClient {
  constructor(config: ExchangeClientConfig) {
    super(config);
  }

  protected override getNotInitializedMessage(): string {
    return "FiClient not initialized. Call initialize() first.";
  }

  /**
   * Refresh nonce from server (called on nonce errors). Public for retry logic.
   */
  override async refreshNonceFromServer(): Promise<void> {
    await super.refreshNonceFromServer();
    console.log("[FiClient] Refreshed nonce from server", {
      walletAddress: this.getWalletAddress(),
      newNonce: this.nonceCounter,
    });
  }

  /**
   * Build order submission payload following buildOrderSubmissionPayload pattern
   * Creates payload with action, marketId, bucketId, side, orderType, price, quantity
   * Converts price and quantity from decimal numbers to satoshi strings
   */
  private buildOrderPayload(params: PlaceOrderParams): Record<string, any> {
    // Convert price and quantity from decimal numbers to satoshi strings
    const priceStr = toSatoshi(params.price);
    const quantityStr = toSatoshi(params.quantity);

    // Build payload following buildOrderSubmissionPayload pattern
    const payload: Record<string, any> = {
      action: OpSubmitOrder,
      marketId: params.marketId,
      bucketId: params.bucketId, // Required: Bucket ID for order placement
      side: params.side.toUpperCase(),
      orderType: params.orderType.toUpperCase(),
      price: priceStr, // Satoshi format string
      quantity: quantityStr, // Satoshi format string
    };

    // Add optional advanced parameters (all in satoshi format)
    if (params.leverage) {
      payload.leverage = params.leverage;
    }
    if (params.takeProfit) {
      payload.takeProfit = params.takeProfit;
    }
    if (params.stopLoss) {
      payload.stopLoss = params.stopLoss;
    }

    return payload;
  }

  /**
   * EIP-712 payload for POST /tx/v1/submit place-order (clob::submit_order).
   * Matches Go E2E: fillType, marginMode, sltp, stopPrice, metadata, timeInForce.
   */
  private buildOrderPayloadForTx(params: PlaceOrderParams): Record<string, any> {
    const priceStr = toSatoshi(params.price);
    const quantityStr = toSatoshi(params.quantity);
    const orderType = params.orderType.toUpperCase();
    const timeInForce = (params.timeInForce || "GTC").toUpperCase();
    const fillType = orderType === "MARKET" ? "IOC" : "GTC";
    const marginMode = params.bucketId ? "isolated" : "cross";
    const sltpParts: string[] = [];
    if (params.stopLoss) sltpParts.push(`sl:${params.stopLoss}`);
    if (params.takeProfit) sltpParts.push(`tp:${params.takeProfit}`);
    const sltp = sltpParts.length ? sltpParts.join(",") : "";
    const leverage = params.leverage || "100000000";

    return {
      action: OpSubmitOrder,
      address: this.getWalletAddress(),
      marketIndex: params.marketId,
      side: params.side.toUpperCase(),
      orderType: orderType === "MARKET" ? "ORDER_TYPE_MARKET" : "ORDER_TYPE_LIMIT",
      price: priceStr,
      stopPrice: "0",
      quantity: quantityStr,
      leverage,
      fillType,
      marginMode,
      bucketId: params.bucketId,
      metadata: "",
      sltp,
      timeInForce,
    };
  }

  /**
   * Place order via POST /tx/v1/submit (E2E/consensus path). Commits nonce only when accepted === true.
   * On errorMessage containing "duplicate transaction", refreshes nonce and retries once.
   */
  private async placeOrderViaTx(
    params: PlaceOrderParams,
    isRetry = false,
  ): Promise<PlaceOrderResponse> {
    const walletAddress = this.getWalletAddress();
    const payload = this.buildOrderPayloadForTx(params);
    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);
    const deadlineRFC3339 = new Date(deadlineNum * 1000)
      .toISOString()
      .replace(/\.\d{3}Z$/, "Z");
    const timestamp = toRFC3339Nano();
    const priceStr = completeMessage.price as string;
    const quantityStr = completeMessage.quantity as string;
    const orderType =
      params.orderType.toUpperCase() === "MARKET"
        ? "ORDER_TYPE_MARKET"
        : "ORDER_TYPE_LIMIT";
    const side =
      params.side.toUpperCase() === "BUY" ? "SIDE_BUY" : "SIDE_SELL";
    const timeInForce = (params.timeInForce || "GTC").toUpperCase();
    const timeInForceProto =
      timeInForce === "IOC"
        ? "TIME_IN_FORCE_IOC"
        : timeInForce === "FOK"
          ? "TIME_IN_FORCE_FOK"
          : timeInForce === "GTT" || timeInForce === "GTD"
            ? "TIME_IN_FORCE_GTT"
            : "TIME_IN_FORCE_GTC";

    const clobMessage: Record<string, unknown> = {
      "@type": "/clob.v1.MsgPlaceOrderRequest",
      type: 2,
      fromAddress: walletAddress,
      address: walletAddress,
      marketIndex: parseInt(params.marketId, 10) || params.marketId,
      symbol: params.marketId,
      price: priceStr,
      quantity: quantityStr,
      size: quantityStr,
      side,
      orderType,
      bucketId: params.bucketId,
      timeInForce: timeInForceProto,
      is_buy: params.side.toUpperCase() === "BUY",
      timestamp,
    };
    if (params.leverage) (clobMessage as Record<string, unknown>).leverage = params.leverage;
    if (params.takeProfit) (clobMessage as Record<string, unknown>).takeProfit = params.takeProfit;
    if (params.stopLoss) (clobMessage as Record<string, unknown>).stopLoss = params.stopLoss;
    if (params.postOnly) (clobMessage as Record<string, unknown>).postOnly = true;
    if (params.hidden) (clobMessage as Record<string, unknown>).hidden = true;
    if (params.displayQuantity) (clobMessage as Record<string, unknown>).displayQuantity = params.displayQuantity;

    const sigBase64 = this.hexSignatureToBase64(signature);
    const txRequest = {
      tx: {
        body: {
          messages: [clobMessage],
          memo: `place_order_${Math.floor(Date.now() / 1000)}_${completeMessage.nonce}`,
          timeoutTimestamp: deadlineRFC3339,
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const body = (response.body ?? {}) as Record<string, unknown>;
    const txHash =
      (body.tx_hash as string) ?? (body.txhash as string) ?? undefined;
    const orderId = (body.orderId as string) ?? undefined;
    const accepted = body.accepted === true;
    const errorMessage = (body.errorMessage as string) ?? "";

    if (accepted && this.walletAddress) {
      const nonceUsed = parseInt(String(completeMessage.nonce), 10);
      await this.getNonceManager().commitNonce(this.walletAddress!, nonceUsed, false);
    } else if (this.walletAddress && (body.tx_hash ?? body.txhash)) {
      await this.refreshNonceFromServer();
    }

    // Duplicate transaction: refresh nonce, ensure next nonce is correct, retry once
    if (
      !isRetry &&
      !accepted &&
      isDuplicateTransactionError(errorMessage) &&
      this.walletAddress
    ) {
      await this.refreshNonceFromServer();
      return this.placeOrderViaTx(params, true);
    }

    return {
      success: accepted,
      orderId: orderId ?? "",
      errorMessage: accepted ? "" : errorMessage,
      ...(txHash != null && { txHash }),
    } as PlaceOrderResponse;
  }

  /**
   * Legacy place order via POST /clob/v1/order (flat body).
   */
  private async placeOrderLegacy(params: PlaceOrderParams): Promise<PlaceOrderResponse> {
    const payload = this.buildOrderPayload(params);
    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const timestamp = Math.floor(Date.now() / 1000) - 5;
    const isBuy = params.side.toUpperCase() === "BUY";
    const orderType = params.orderType.toUpperCase();
    const apiType =
      orderType === "MARKET" ? "ORDER_TYPE_MARKET" : "ORDER_TYPE_LIMIT";

    const orderReq: Record<string, any> = {
      address: this.getWalletAddress(),
      market_index: params.marketId,
      bucket_id: params.bucketId,
      price: completeMessage.price,
      quantity: completeMessage.quantity,
      type: apiType,
      signature,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      timestamp,
      asset_pair: params.marketId,
      market_id: params.marketId,
      size: completeMessage.quantity,
      is_buy: isBuy,
      order_type: orderType,
      client_order_id: `order-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
      time_in_force: params.timeInForce || "GTC",
    };
    if (params.leverage) orderReq.leverage = params.leverage;
    if (params.takeProfit) orderReq.take_profit = params.takeProfit;
    if (params.stopLoss) orderReq.stop_loss = params.stopLoss;
    if (params.postOnly) orderReq.post_only = true;
    if (params.hidden) orderReq.hidden = true;
    if (params.displayQuantity) orderReq.display_quantity = params.displayQuantity;
    if (params.reduceOnly) orderReq.reduce_only = true;

    const response = await this.httpClient.post("/clob/v1/order", orderReq);
    const body = response.body as PlaceOrderResponse & { order_id?: string; txhash?: string };
    const orderId = body.orderId;
    const txHash = body.txhash != null ? String(body.txhash) : body.txHash;
    return {
      ...body,
      orderId: orderId ?? body.orderId,
      ...(txHash != null && { txHash }),
    } as PlaceOrderResponse;
  }

  /**
   * Place an order. Default: POST /tx/v1/submit (E2E/consensus). Set params.useLegacyClobOrder for POST /clob/v1/order.
   *
   * @param params - Order parameters (price/quantity converted to satoshi in payload builders)
   * @returns API response
   */
  async placeOrder(params: PlaceOrderParams): Promise<PlaceOrderResponse> {
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    if (
      !params.side ||
      (params.side.toUpperCase() !== "BUY" &&
        params.side.toUpperCase() !== "SELL")
    ) {
      throw new Error("side must be BUY or SELL");
    }
    if (
      !params.orderType ||
      (params.orderType.toUpperCase() !== "LIMIT" &&
        params.orderType.toUpperCase() !== "MARKET")
    ) {
      throw new Error("orderType must be LIMIT or MARKET");
    }
    if (!params.price || params.price <= 0) {
      throw new Error("price must be positive");
    }
    if (!params.quantity || params.quantity <= 0) {
      throw new Error("quantity must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    if (params.useLegacyClobOrder) {
      return this.placeOrderLegacy(params);
    }
    return this.placeOrderViaTx(params);
  }

  /**
   * Place multiple orders in a single batch request
   * POST /clob/v1/orders/batch
   * Supports 1-100 orders per batch
   *
   * @param params - Batch order parameters
   * @returns Batch order response with success/failure for each order
   */
  async placeBatchOrders(
    params: BatchOrderParams,
  ): Promise<BatchOrderResponse> {
    // Input validation
    if (!params.orders || !Array.isArray(params.orders)) {
      throw new Error("orders must be a non-empty array");
    }
    if (params.orders.length === 0) {
      throw new Error("At least one order is required");
    }
    if (params.orders.length > 100) {
      throw new Error("Maximum 100 orders per batch");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const timestamp = Math.floor(Date.now() / 1000) - 5; // 5 seconds in the past for clock skew

    // Build batch orders array
    const batchOrders = await Promise.all(
      params.orders.map(async (orderParams, index) => {
        // Validate each order
        if (!orderParams.marketId || orderParams.marketId.trim() === "") {
          throw new Error(`Order ${index}: marketId cannot be empty`);
        }
        if (!orderParams.bucketId || orderParams.bucketId.trim() === "") {
          throw new Error(`Order ${index}: bucketId cannot be empty`);
        }
        if (
          !orderParams.side ||
          (orderParams.side.toUpperCase() !== "BUY" &&
            orderParams.side.toUpperCase() !== "SELL")
        ) {
          throw new Error(`Order ${index}: side must be BUY or SELL`);
        }
        if (
          !orderParams.orderType ||
          (orderParams.orderType.toUpperCase() !== "LIMIT" &&
            orderParams.orderType.toUpperCase() !== "MARKET")
        ) {
          throw new Error(`Order ${index}: orderType must be LIMIT or MARKET`);
        }
        if (!orderParams.price || orderParams.price <= 0) {
          throw new Error(`Order ${index}: price must be positive`);
        }
        if (!orderParams.quantity || orderParams.quantity <= 0) {
          throw new Error(`Order ${index}: quantity must be positive`);
        }

        // Build order payload (same as placeOrder)
        const payload = this.buildOrderPayload(orderParams);
        const signatureBuilder = this.getSignatureBuilder();
        const completeMessage = await signatureBuilder.buildCompleteMessage(
          payload,
          this.getNonceProvider(),
        );
        const signature = await signatureBuilder.signPayload(completeMessage);

        const isBuy = orderParams.side.toUpperCase() === "BUY";
        const orderType = orderParams.orderType.toUpperCase();
        const apiType =
          orderType === "MARKET" ? "ORDER_TYPE_MARKET" : "ORDER_TYPE_LIMIT";
        const clientOrderId = `batch-${Date.now()}-${index}-${Math.floor(Math.random() * 1000000)}`;
        const sizeOrQuantity = completeMessage.quantity;
        const sideStr = isBuy ? "BUY" : "SELL";

        // Align with e2e request shape: symbol, quantity, side, orderType (camelCase), bucketId (camelCase).
        // Keep size + is_buy for router batch handler (createPlaceOrderRequest) compatibility.
        const orderReq: Record<string, any> = {
          address: walletAddress,
          symbol: orderParams.marketId,
          bucketId: orderParams.bucketId,
          price: completeMessage.price,
          size: sizeOrQuantity,
          quantity: sizeOrQuantity,
          is_buy: isBuy,
          side: sideStr,
          orderType: orderType,
          type: apiType,
          signature: signature,
          nonce: completeMessage.nonce,
          deadline: completeMessage.deadline,
          timestamp: timestamp,
          client_order_id: clientOrderId,
        };

        // Add optional parameters (camelCase for router batch handler; snake_case for e2e parity)
        if (orderParams.leverage) {
          orderReq.leverage = orderParams.leverage;
        }
        if (orderParams.takeProfit) {
          orderReq.take_profit = orderParams.takeProfit;
        }
        if (orderParams.stopLoss) {
          orderReq.stop_loss = orderParams.stopLoss;
        }
        if (orderParams.postOnly) {
          orderReq.post_only = true;
          orderReq.postOnly = true;
        }
        if (orderParams.hidden) {
          orderReq.hidden = true;
        }
        if (orderParams.displayQuantity) {
          orderReq.display_quantity = orderParams.displayQuantity;
          orderReq.displayQuantity = orderParams.displayQuantity;
        }
        if (orderParams.reduceOnly) {
          orderReq.reduce_only = true;
        }

        return orderReq;
      }),
    );

    // Submit batch to /clob/v1/orders/batch endpoint
    const response = await this.httpClient.post("/clob/v1/orders/batch", {
      orders: batchOrders,
    });

    return response.body as BatchOrderResponse;
  }

  /**
   * Cancel an order using RESTful DELETE endpoint.
   * DELETE /clob/v1/order/{order_id}?address={address}.
   * E2E uses POST /tx/v1/submit with signed TxRequest instead; prefer cancelOrder() for alignment.
   *
   * @param orderId - Order ID to cancel
   * @param address - Optional wallet address (defaults to initialized wallet)
   * @returns Cancel order response
   */
  async cancelOrderRESTful(
    orderId: string,
    address?: string,
  ): Promise<CancelOrderResponse> {
    // Input validation
    if (!orderId || orderId === "") {
      throw new Error("orderId cannot be empty");
    }

    if (!this.walletAddress && !address) {
      await this.initialize();
    }

    const walletAddress = address || this.getWalletAddress();

    // DELETE /clob/v1/order/{order_id}?address={address}
    const response = await this.httpClient.delete(
      `/clob/v1/order/${encodeURIComponent(orderId)}?address=${encodeURIComponent(walletAddress)}`,
    );

    return response.body as CancelOrderResponse;
  }

  /**
   * Cancel an order via POST /tx/v1/submit (same ingress pipeline as E2E).
   * Builds Cosmos-style Tx with /clob.v1.MsgCancelOrderRequest (type 43), auth_info, signatures;
   * maps SubmitTxResponse (accepted, txhash/tx_hash, errorMessage) to CancelOrderResponse.
   * Commits nonce only when accepted === true. On nonce error (thrown) or errorMessage
   * "duplicate transaction": refreshes nonce and retries once.
   */
  async cancelOrder(params: CancelOrderParams): Promise<CancelOrderResponse> {
    if (!params.orderId || params.orderId === "") {
      throw new Error("orderId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const payload = {
      action: OpCancelOrder,
      owner: walletAddress,
      address: walletAddress,
      orderId: params.orderId,
      marketIndex: params.marketId,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);

    const timestamp = toRFC3339Nano();
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);
    const deadlineRFC3339 = new Date(deadlineNum * 1000)
      .toISOString()
      .replace(/\.\d{3}Z$/, "Z");

    const clobMessage: Record<string, unknown> = {
      "@type": "/clob.v1.MsgCancelOrderRequest",
      type: 43,
      fromAddress: walletAddress,
      address: walletAddress,
      orderId: params.orderId,
      symbol: params.marketId,
      timestamp,
    };

    const sigBase64 = this.hexSignatureToBase64(signature);
    const txRequest = {
      tx: {
        body: {
          messages: [clobMessage],
          memo: `cancel_order_${params.orderId}_${Math.floor(Date.now() / 1000)}`,
          timeoutTimestamp: deadlineRFC3339,
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    try {
      const response = await this.httpClient.post("/tx/v1/submit", txRequest);
      const body = (response.body ?? {}) as Record<string, unknown>;
      const out = this.mapSubmitTxToCancelOrderResponse(body, params.orderId);
      if (out.success && this.walletAddress) {
        const nonceUsed = parseInt(String(completeMessage.nonce), 10);
        await this.getNonceManager().commitNonce(this.walletAddress!, nonceUsed, false);
      } else if (
        this.walletAddress &&
        (body.txhash ?? body.tx_hash ?? body.Txhash)
      ) {
        await this.refreshNonceFromServer();
      }
      // Duplicate transaction: refresh nonce and retry once (next nonce used on retry)
      if (
        !out.success &&
        isDuplicateTransactionError(out.errorMessage) &&
        this.walletAddress
      ) {
        await this.refreshNonceFromServer();
        const retryCompleteMessage =
          await signatureBuilder.buildCompleteMessage(
            payload,
            this.getNonceProvider(),
          );
        const retrySignature =
          await signatureBuilder.signPayload(retryCompleteMessage);
        const retryDeadlineNum = parseInt(
          String(retryCompleteMessage.deadline),
          10,
        );
        const retryDeadlineRFC3339 = new Date(retryDeadlineNum * 1000)
          .toISOString()
          .replace(/\.\d{3}Z$/, "Z");
        const retryMessage: Record<string, unknown> = {
          "@type": "/clob.v1.MsgCancelOrderRequest",
          type: 43,
          fromAddress: walletAddress,
          address: walletAddress,
          orderId: params.orderId,
          symbol: params.marketId,
          timestamp: toRFC3339Nano(),
        };
        const retryTxRequest = {
          tx: {
            body: {
              messages: [retryMessage],
              memo: `cancel_order_${params.orderId}_${Math.floor(Date.now() / 1000)}`,
              timeoutTimestamp: retryDeadlineRFC3339,
            },
            auth_info: {
              signer_infos: [
                {
                  nonce: parseInt(String(retryCompleteMessage.nonce), 10),
                  deadline: retryDeadlineNum,
                  timestamp: toRFC3339Nano(),
                },
              ],
            },
            signatures: [this.hexSignatureToBase64(retrySignature)],
          },
        };
        const retryResponse = await this.httpClient.post(
          "/tx/v1/submit",
          retryTxRequest,
        );
        const retryBody = (retryResponse.body ?? {}) as Record<string, unknown>;
        const retryOut = this.mapSubmitTxToCancelOrderResponse(
          retryBody,
          params.orderId,
        );
        if (retryOut.success && this.walletAddress) {
          const nonceUsed = parseInt(String(retryCompleteMessage.nonce), 10);
          await this.getNonceManager().commitNonce(this.walletAddress!, nonceUsed, false);
        } else if (
          this.walletAddress &&
          (retryBody.txhash ?? retryBody.tx_hash ?? retryBody.Txhash)
        ) {
          await this.refreshNonceFromServer();
        }
        return retryOut;
      }
      return out;
    } catch (error) {
      if (shouldRefreshNonce(error)) {
        await this.refreshNonceFromServer();
        const retryCompleteMessage =
          await signatureBuilder.buildCompleteMessage(
            payload,
            this.getNonceProvider(),
          );
        const retrySignature =
          await signatureBuilder.signPayload(retryCompleteMessage);
        const retryDeadlineNum = parseInt(
          String(retryCompleteMessage.deadline),
          10,
        );
        const retryDeadlineRFC3339 = new Date(retryDeadlineNum * 1000)
          .toISOString()
          .replace(/\.\d{3}Z$/, "Z");
        const retryMessage: Record<string, unknown> = {
          "@type": "/clob.v1.MsgCancelOrderRequest",
          type: 43,
          fromAddress: walletAddress,
          address: walletAddress,
          orderId: params.orderId,
          symbol: params.marketId,
          timestamp: toRFC3339Nano(),
        };
        const retryTxRequest = {
          tx: {
            body: {
              messages: [retryMessage],
              memo: `cancel_order_${params.orderId}_${Math.floor(Date.now() / 1000)}`,
              timeoutTimestamp: retryDeadlineRFC3339,
            },
            auth_info: {
              signer_infos: [
                {
                  nonce: parseInt(String(retryCompleteMessage.nonce), 10),
                  deadline: retryDeadlineNum,
                  timestamp: toRFC3339Nano(),
                },
              ],
            },
            signatures: [this.hexSignatureToBase64(retrySignature)],
          },
        };
        const retryResponse = await this.httpClient.post(
          "/tx/v1/submit",
          retryTxRequest,
        );
        const retryBody = (retryResponse.body ?? {}) as Record<string, unknown>;
        const retryOut = this.mapSubmitTxToCancelOrderResponse(
          retryBody,
          params.orderId,
        );
        if (retryOut.success && this.walletAddress) {
          const nonceUsed = parseInt(String(retryCompleteMessage.nonce), 10);
          await this.getNonceManager().commitNonce(this.walletAddress!, nonceUsed, false);
        } else if (
          this.walletAddress &&
          (retryBody.txhash ?? retryBody.tx_hash ?? retryBody.Txhash)
        ) {
          await this.refreshNonceFromServer();
        }
        return retryOut;
      }
      throw error;
    }
  }

  /**
   * Map SubmitTxResponse (POST /tx/v1/submit) to CancelOrderResponse.
   * Ingress/E2E return accepted, txhash (or tx_hash / Txhash). Commit nonce only when accepted === true (caller does it).
   */
  private mapSubmitTxToCancelOrderResponse(
    body: {
      accepted?: boolean;
      txhash?: string;
      tx_hash?: string;
      Txhash?: string;
      errorMessage?: string;
      [key: string]: unknown;
    },
    orderId: string,
  ): CancelOrderResponse {
    const accepted = body.accepted === true;
    const txhash =
      body.txhash != null
        ? String(body.txhash)
        : body.tx_hash != null
          ? String(body.tx_hash)
          : body.Txhash != null
            ? String(body.Txhash)
            : "";
    const nowMs = Date.now();
    const resultTimestamp = {
      seconds: Math.floor(nowMs / 1000),
      nanos: (nowMs % 1000) * 1e6,
    };
    return {
      ...body,
      success: accepted,
      txhash,
      result: {
        orderId,
        success: accepted,
        timestamp: resultTimestamp,
      },
      timestamp: resultTimestamp,
      ...(body.errorMessage != null && {
        errorMessage: String(body.errorMessage),
      }),
    } as CancelOrderResponse;
  }

  /**
   * Modify an order
   * Follows ModifyOrder pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async modifyOrder(params: ModifyOrderParams): Promise<ModifyOrderResponse> {
    // Input validation (following Go implementation)
    if (!params.orderId || params.orderId === "") {
      throw new Error("orderId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    // Validate price and quantity (already numbers, no conversion needed)
    if (!params.newPrice || params.newPrice <= 0) {
      throw new Error("newPrice must be positive");
    }
    if (!params.newQuantity || params.newQuantity <= 0) {
      throw new Error("newQuantity must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload aligned with e2e/Go: common/tx/clob BuildModifyOrderTx and EIP-712 use marketIndex (not marketId).
    // Router ClobModifyOrderHandler reads address/owner, orderId, newPrice, newQuantity; marketIndex is in signed message for type consistency.
    const payload: Record<string, any> = {
      action: OpModifyOrder,
      orderId: params.orderId,
      marketIndex: params.marketId, // EIP-712 and e2e use marketIndex; SDK params keep marketId for API consistency
      newPrice: toSatoshi(params.newPrice),
      newQuantity: toSatoshi(params.newQuantity),
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );

    // Generate signature using generateSingleSignature (fixes recovery ID issue)
    const signature = await signatureBuilder.signPayload(completeMessage);

    // Build EIP712Tx structure (following buildEIP712Tx pattern)
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Change leverage for a position.
   * Aligns with E2E flow: standards EIP712Types expect action, owner, address, positionId, bucketId, marketIndex, newLeverage, nonce, deadline.
   * Submits via POST /router/v1/tx (EIP712); E2E uses POST /tx/v1/submit (Cosmos Tx) with same logical message.
   */
  async changeLeverage(
    params: ChangeLeverageParams,
  ): Promise<ChangeLeverageResponse> {
    if (params.newLeverage === undefined) {
      throw new Error("newLeverage must be provided");
    }
    const hasMarket =
      params.marketIndex !== undefined ||
      (params.marketId != null && params.marketId.trim() !== "");
    if (!hasMarket) {
      throw new Error("marketIndex or marketId must be provided");
    }
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const address = this.getWalletAddress();
    const marketIndexStr =
      params.marketIndex !== undefined
        ? String(params.marketIndex)
        : String(parseInt(params.marketId!.trim(), 10));

    // Payload matches standards payload/bucket.go EIP712Types: action, owner, address, positionId, bucketId, marketIndex, newLeverage, nonce, deadline (owner/nonce/deadline added by buildCompleteMessage).
    const payload: Record<string, unknown> = {
      action: OpUpdatePositionLeverage,
      address,
      positionId: params.positionId ?? "",
      bucketId: params.bucketId,
      marketIndex: marketIndexStr,
      newLeverage: toSatoshi(params.newLeverage),
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }
  /**
   * Close a position
   * Follows ClosePosition pattern from bucket/position_close_test.go
   * Uses /bucket/v1/position/close endpoint with EIP-712 signing
   *
   * EIP-712 payload structure (matching Go test):
   * - address (not owner)
   * - marketId (*big.Int as string)
   * - closeSize (*big.Int as string)
   * - marketPrice (*big.Int as string)
   * - nonce (uint64)
   * - deadline (uint64)
   * - NO action field in payload (domain is "bucket::close_position")
   *
   * Expected response structure (from position_close_test.go):
   * { success: boolean, error_message?: string, transactionHash?: string }
   */
  async closePosition(
    params: ClosePositionParams,
  ): Promise<ClosePositionResponse> {
    // Input validation
    if (!params.positionId || params.positionId.trim() === "") {
      throw new Error("positionId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!this.walletAddress) {
      await this.initialize();
    }

    const address = this.getWalletAddress();
    const now = Math.floor(Date.now() / 1000);
    const deadline = now + 300; // 5 minutes from now (matching Go test)
    const nonce = await this.getNextNonce();

    // Fetch market price if not provided (required for close position)
    let marketPriceValue: number;
    if (params.marketPrice && params.marketPrice > 0) {
      marketPriceValue = params.marketPrice;
    } else {
      // Fetch current market price from ticker
      try {
        const ticker = await this.infoClient.getMarketStats(params.marketId);
        if (!ticker || !ticker.lastPrice) {
          throw new Error(
            "marketPrice is required and could not be fetched from ticker",
          );
        }
        // Convert from satoshi string to decimal number
        marketPriceValue = fromSatoshi(ticker.lastPrice);
      } catch (error) {
        throw new Error(
          `Failed to fetch market price: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    }

    // Convert market price to satoshi string
    const marketPrice = toSatoshi(marketPriceValue).toString();

    // Handle quantity: 0 or undefined means close full position (per Go test)
    // If quantity is provided and > 0, use it; otherwise use "0" for full close
    const closeSize =
      params.quantity && params.quantity > 0
        ? toSatoshi(params.quantity).toString()
        : "0"; // "0" means close full position per Go test (line 424, 429)

    // Build EIP-712 payload matching Go test structure exactly
    // EIP-712 schema: address, marketId, closeSize, marketPrice, nonce, deadline
    // NOTE: NO 'action' field in payload (domain "bucket::close_position" is handled by signing)
    // NOTE: NO 'owner' field - uses 'address' instead
    const eip712Payload: Record<string, any> = {
      action: OpClosePosition,
      address: address,
      marketId: params.marketId,
      closeSize: closeSize,
      marketPrice: marketPrice,
      nonce: nonce,
      deadline: deadline,
    };

    // Sign the payload using signPayload from BaseSignatureBuilder
    // The domain "bucket::close_position" is handled by the signing infrastructure
    const signatureBuilder = this.getSignatureBuilder();
    const signature = await signatureBuilder.signPayload(eip712Payload);

    // Build request body matching Go ClosePositionRequest structure exactly
    // Reference: bucket/position_close_test.go
    // JSON field names match protobuf json tags (snake_case)
    const closeRequest: Record<string, any> = {
      address: address, // User's blockchain address (for signature verification)
      position_id: params.positionId, // Position identifier (required by API)
      market_id: params.marketId, // uint64 as string
      close_size: closeSize, // u256 as satoshi-format string (1e8)
      market_price: marketPrice, // u256 as satoshi-format string (1e8)
      timestamp: now, // int64
      sign_type: 1, // EIP712 (v1.SignType enum)
      nonce: nonce.toString(), // string
      chain_id: this.chainID.toString(), // string
      deadline: deadline.toString(), // string
      signature: signature, // string
      // Optional multisig fields (not included in basic request)
      // multi_signatures: [],
      // threshold: 0,
    };

    // Submit to /bucket/v1/position/close endpoint (matching Go test)
    const response = await this.httpClient.post(
      "/bucket/v1/position/close",
      closeRequest,
    );
 
    // Response should match ClosePositionResponse structure:
    // { success: boolean, error_message?: string, transactionHash?: string, ... }
    return response.body as ClosePositionResponse;
  }

  /**
   * Close a position via POST /router/v1/tx (EIP712Tx).
   * Payload is aligned with router ClosePositionHandler and E2E BuildClosePositionTx:
   * - marketIndex (number): router uses getInt64(payload, "marketIndex")
   * - closeSize, marketPrice as decimal numbers: router uses getFloat64 then DecimalToSatoshi
   * - timestamp (Unix seconds): required for MsgClosePositionRequest.Timestamp
   * E2E uses POST /tx/v1/submit with a full Tx (body.messages + auth_info + signatures);
   * this method keeps /router/v1/tx and fixes payload so the router handler receives correct types.
   */
  async closePositionEIP712Tx(
    params: ClosePositionParams,
  ): Promise<ClosePositionResponse> {
    if (!params.positionId || params.positionId.trim() === "") {
      throw new Error("positionId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!this.walletAddress) {
      await this.initialize();
    }

    const marketIndexNum = parseInt(params.marketId, 10);
    if (Number.isNaN(marketIndexNum) || marketIndexNum < 0) {
      throw new Error("marketId must be a non-negative numeric string");
    }

    // closeSize: 0 = full close (e2e-aligned); handler expects decimal (router converts to satoshi)
    const closeSizeDecimal =
      params.quantity != null && params.quantity > 0 ? params.quantity : 0;

    let marketPriceDecimal: number;
    if (params.marketPrice != null && params.marketPrice > 0) {
      marketPriceDecimal = params.marketPrice;
    } else {
      try {
        const ticker = await this.infoClient.getMarketStats(params.marketId);
        if (!ticker?.lastPrice) {
          throw new Error("marketPrice is required and could not be fetched from ticker");
        }
        marketPriceDecimal = fromSatoshi(ticker.lastPrice);
      } catch (e) {
        throw new Error(
          `Failed to resolve market price: ${e instanceof Error ? e.message : String(e)}`,
        );
      }
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const payload: Record<string, unknown> = {
      action: OpClosePosition,
      address: this.getWalletAddress(),
      positionId: params.positionId,
      marketIndex: marketIndexNum,
      closeSize: closeSizeDecimal,
      marketPrice: marketPriceDecimal,
      timestamp: nowSeconds,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    const body = response.body as Record<string, unknown>;

    // Normalize to E2E response shape: accepted → success, txhash, errorMessage
    const accepted = body?.accepted === true;
    const txhash =
      (body?.txhash as string) ?? (body?.tx_hash as string) ?? "";
    const errorMessage =
      (body?.errorMessage as string) ?? (body?.error_message as string) ?? "";

    return {
      ...body,
      success: accepted,
      txhash,
      errorMessage: errorMessage || undefined,
    } as ClosePositionResponse;
  }

  /**
   * Swap tokens
   * Follows Swap pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async swap(params: SwapParams): Promise<any> {
    // Input validation
    if (!params.fromToken || params.fromToken === "") {
      throw new Error("fromToken cannot be empty");
    }
    if (!params.toToken || params.toToken === "") {
      throw new Error("toToken cannot be empty");
    }
    // Validate amounts (already numbers, no conversion needed)
    if (!params.amountIn || params.amountIn <= 0) {
      throw new Error("amountIn must be positive");
    }
    if (!params.minAmountOut || params.minAmountOut <= 0) {
      throw new Error("minAmountOut must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following CreateSwapPayload pattern
    // Convert amounts from decimal numbers to satoshi strings
    const payload: Record<string, any> = {
      action: OpSwap,
      fromToken: params.fromToken,
      toToken: params.toToken,
      amountIn: toSatoshi(params.amountIn), // Convert to satoshi string
      minAmountOut: toSatoshi(params.minAmountOut), // Convert to satoshi string
    };

    // Add optional path if provided
    if (params.path && params.path.length > 0) {
      payload.path = params.path;
    }

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Change margin ratio (market-level).
   * Aligns with standards: action market::change_marginRatio, payload per standards/payload/market.go EIP712Types
   * (action, owner, marketIndex, newInitialMarginRatio, newMaintenanceMarginRatio, reason, nonce, deadline).
   * Submits via POST /router/v1/tx. Note: router eip712req has no handler for this action yet; add one when backend supports it.
   */
  async changeMarginRatioEIP712Tx(
    params: ChangeMarginRatioParams,
  ): Promise<ChangeMarginRatioResponse> {
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (params.reason === undefined || params.reason.trim() === "") {
      throw new Error("reason cannot be empty");
    }
    const initialRatio =
      typeof params.newInitialMarginRatio === "number"
        ? params.newInitialMarginRatio
        : parseFloat(String(params.newInitialMarginRatio));
    const maintenanceRatio =
      typeof params.newMaintenanceMarginRatio === "number"
        ? params.newMaintenanceMarginRatio
        : parseFloat(String(params.newMaintenanceMarginRatio));
    if (Number.isNaN(initialRatio) || initialRatio < 0) {
      throw new Error("newInitialMarginRatio must be a non-negative number");
    }
    if (Number.isNaN(maintenanceRatio) || maintenanceRatio < 0) {
      throw new Error("newMaintenanceMarginRatio must be a non-negative number");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Payload matches standards payload/market.go EIP712Types: action, owner, marketIndex, newInitialMarginRatio, newMaintenanceMarginRatio, reason, nonce, deadline (owner/nonce/deadline added by buildCompleteMessage)
    const payload: Record<string, any> = {
      action: OpChangeMarginRatio,
      marketIndex: params.marketId,
      newInitialMarginRatio: toSatoshi(initialRatio),
      newMaintenanceMarginRatio: toSatoshi(maintenanceRatio),
      reason: params.reason,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Internal transfer between accounts
   * Follows InternTransfer pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async internTransferEIP712Tx(
    params: InternTransferParams,
  ): Promise<InternTransferResponse> {
    // Input validation
    if (!params.fromAccount || params.fromAccount === "") {
      throw new Error("fromAccount cannot be empty");
    }
    if (!params.toAccount || params.toAccount === "") {
      throw new Error("toAccount cannot be empty");
    }
    if (!params.tokenAddress || params.tokenAddress === "") {
      throw new Error("tokenAddress cannot be empty");
    }
    // Validate amount (already number, no conversion needed)
    if (!params.amount || params.amount <= 0) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following CreateInternTransferPayload pattern
    // Convert amount from decimal number to satoshi string
    const payload: Record<string, any> = {
      action: OpInternTransfer,
      fromAccount: params.fromAccount,
      toAccount: params.toAccount,
      amount: toSatoshi(params.amount), // Convert to satoshi string
      tokenAddress: params.tokenAddress,
      address: this.getWalletAddress(),
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Transfer margin between buckets
   * Follows the pattern from transfer_test.go
   * Uses flat request structure with EIP712 signature (not EIP712Tx)
   *
   * @param params - Bucket transfer parameters
   * @returns API response
   */
  async bucketTransfer(
    params: BucketTransferParams,
  ): Promise<BucketTransferResponse> {
    // Input validation
    if (!params.fromBucketId || params.fromBucketId.trim() === "") {
      throw new Error("fromBucketId cannot be empty");
    }
    if (!params.toBucketId || params.toBucketId.trim() === "") {
      throw new Error("toBucketId cannot be empty");
    }
    if (!params.assetId || params.assetId.trim() === "") {
      throw new Error("assetId cannot be empty");
    }
    if (
      params.amount === undefined ||
      (typeof params.amount === "number" && params.amount <= 0)
    ) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    // Convert amount from decimal number to satoshi string
    const amountStr = toSatoshi(params.amount);

    // Signable payload per standards/payload/bucket.go (bank::bucket_transfer): action, owner, sourceBucketId, targetBucketId, amount, nonce, deadline (no "asset").
    const payload: Record<string, any> = {
      action: OpBankBucketTransfer,
      sourceBucketId: params.fromBucketId,
      targetBucketId: params.toBucketId,
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);

    // HTTP body: proto/e2e use camelCase (sourceBucketId, targetBucketId). Router unmarshals MsgTransferBetweenBucketsRequest.
    const timestamp = Math.floor(Date.now() / 1000);
    const reqBody: Record<string, any> = {
      address: walletAddress,
      sourceBucketId: params.fromBucketId,
      targetBucketId: params.toBucketId,
      amount: amountStr,
      reason: "sdk_transfer",
      timestamp,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      signature,
      chain_id: this.chainID.toString(),
    };

    // Submit to /bucket/v1/bucket/transfer endpoint with flat structure
    const response = await this.httpClient.post(
      "/bucket/v1/bucket/transfer",
      reqBody,
    );
    return response.body;
  }

  /**
   * Extract spot balance to bucket (bank → bucket).
   * E2E flow standard: ingress path only (router/docs/improvements/bank_transfer_to_bucket_signable_vs_http_body.md).
   * Signable message (EIP-712): action, owner, bucketId, assetIndex, amount, nonce, deadline — used only for signing/verification.
   * Tx body message: proto-only (address, bucketId, assetIndex, amount, timestamp) plus @type/operation; nonce/deadline live in AuthInfo, signature in TxRequest.signatures.
   *
   * @param params - Extract to bucket parameters
   * @returns API response
   */
  async extractToBucket(
    params: ExtractToBucketParams,
  ): Promise<ExtractToBucketResponse> {
    // Input validation
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    const assetIndex =
      params.assetIndex ??
      (params.assetId?.toUpperCase() === "USDC" ? 1 : undefined);
    if (assetIndex == null || assetIndex < 1) {
      throw new Error(
        "Provide assetIndex (e.g. 1 for USDC) or assetId 'USDC'",
      );
    }
    if (!params.amount || params.amount <= 0) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const amountStr = toSatoshi(params.amount);

    // Signable payload per standards: action, owner, bucketId, assetIndex, amount, nonce, deadline (owner/nonce/deadline added by buildCompleteMessage)
    const payload: Record<string, unknown> = {
      action: OpBankTransferToBucket,
      bucketId: params.bucketId,
      assetIndex: Number(assetIndex),
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const deadlineOffset = 300; // 5 minutes, matches common/tx/bucket BuildTransferToBucketTx
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
      deadlineOffset,
    );

    const signature = await signatureBuilder.signPayload(completeMessage);

    // Timestamp for proto message and auth_info (RFC3339-style; ingress expects it for verification)
    const timestamp = toRFC3339Nano();
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);

    // Body message: proto-only (MsgTransferToBucketRequest). No nonce, deadline, signature — doc §3, §4.
    const bankMessage: Record<string, unknown> = {
      "@type": "/bank.v1.MsgTransferToBucketRequest",
      operation: "transfer_to_bucket",
      address: walletAddress,
      bucketId: params.bucketId,
      assetIndex: Number(assetIndex),
      amount: amountStr,
      timestamp,
    };

    // Ingress expects signatures as base64 (proto bytes); convert if hex
    const sigBase64 = this.hexSignatureToBase64(signature);

    const txRequest = {
      tx: {
        body: {
          messages: [bankMessage],
          memo: `transfer_to_bucket_${Math.floor(Date.now() / 1000)}_${completeMessage.nonce}`,
          timeoutTimestamp: new Date(deadlineNum * 1000).toISOString().replace(/\.\d{3}Z$/, "Z"),
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const body = (response.body ?? {}) as Record<string, unknown>;
    const result = this.mapSubmitTxToExtractToBucketResponse(body);

    // Duplicate transaction: refresh nonce and retry once
    if (
      !result.success &&
      isDuplicateTransactionError(result.errorMessage) &&
      this.walletAddress
    ) {
      await this.refreshNonceFromServer();
      const retryMessage = await signatureBuilder.buildCompleteMessage(
        payload,
        this.getNonceProvider(),
        deadlineOffset,
      );
      const retrySignature =
        await signatureBuilder.signPayload(retryMessage);
      const retryDeadlineNum = parseInt(String(retryMessage.deadline), 10);
      const retryTxRequest = {
        tx: {
          body: {
            messages: [
              {
                "@type": "/bank.v1.MsgTransferToBucketRequest",
                operation: "transfer_to_bucket",
                address: walletAddress,
                bucketId: params.bucketId,
                assetIndex: Number(assetIndex),
                amount: amountStr,
                timestamp: toRFC3339Nano(),
              },
            ],
            memo: `transfer_to_bucket_${Math.floor(Date.now() / 1000)}_${retryMessage.nonce}`,
            timeoutTimestamp: new Date(retryDeadlineNum * 1000)
              .toISOString()
              .replace(/\.\d{3}Z$/, "Z"),
          },
          auth_info: {
            signer_infos: [
              {
                nonce: parseInt(String(retryMessage.nonce), 10),
                deadline: retryDeadlineNum,
                timestamp: toRFC3339Nano(),
              },
            ],
          },
          signatures: [this.hexSignatureToBase64(retrySignature)],
        },
      };
      const retryResponse = await this.httpClient.post(
        "/tx/v1/submit",
        retryTxRequest,
      );
      const retryBody = (retryResponse.body ?? {}) as Record<string, unknown>;
      return this.mapSubmitTxToExtractToBucketResponse(retryBody);
    }

    return result;
  }

  /**
   * Map SubmitTxResponse (POST /tx/v1/submit) to ExtractToBucketResponse.
   * Canonical JSON field from router is txhash; SDK exposes it as txHash.
   */
  private mapSubmitTxToExtractToBucketResponse(body: {
    accepted?: boolean;
    txhash?: string;
    errorMessage?: string;
    [key: string]: unknown;
  }): ExtractToBucketResponse {
    const accepted = body.accepted === true;
    const txHash = body.txhash != null ? String(body.txhash) : undefined;
    const timestamp = toRFC3339Nano();
    return {
      success: accepted,
      ...(txHash != null && { txHash }),
      ...(body.errorMessage != null && { errorMessage: String(body.errorMessage) }),
      timestamp,
      ...body,
    };
  }

  /**
   * Create a margin bucket. Aligned with router/docs/improvements/create_bucket_signable_vs_http_body.md:
   * - Signable payload (EIP-712): action, owner, collateralAssetIndex, initialMargin, marginType, nonce, deadline (standards).
   * - HTTP body for POST /bucket/v1/bucket: proto-only, camelCase (MsgCreateBucketRequest); no nonce, deadline, signature, chain_id.
   */
  async createBucket(
    params: CreateBucketParams,
  ): Promise<CreateBucketResponse> {
    if (
      params.collateralAssetIndex === undefined ||
      typeof params.collateralAssetIndex !== "number" ||
      params.collateralAssetIndex < 0
    ) {
      throw new Error("collateralAssetIndex must be a non-negative number");
    }
    if (!params.marginType || params.marginType.trim() === "") {
      throw new Error("marginType cannot be empty");
    }
    const marginTypeNorm = params.marginType.toLowerCase();
    if (marginTypeNorm !== "cross" && marginTypeNorm !== "isolated") {
      throw new Error("marginType must be 'cross' or 'isolated'");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const timestamp = toRFC3339Nano();
    const initialMargin = params.initialMargin ?? "0";

    // Signable payload per standards: action, owner, collateralAssetIndex, initialMargin, marginType, nonce, deadline
    const payload: Record<string, unknown> = {
      action: OpCreateMarginBucket,
      collateralAssetIndex: String(params.collateralAssetIndex),
      initialMargin,
      marginType: marginTypeNorm,
    };

    const signatureBuilder = this.getSignatureBuilder();
    await signatureBuilder.buildCompleteMessage(
      payload as Record<string, any>,
      this.getNonceProvider(),
      3600,
    );
    // Signature not sent in direct POST body; router unmarshals proto-only. Signing kept for future ingress/wrapper use.

    // Proto-only body (camelCase): address, bucketId?, bucketType, collateralAssetIndex, initialMargin, timestamp
    const bucketTypeEnum =
      marginTypeNorm === "isolated"
        ? "BUCKET_TYPE_ISOLATED"
        : "BUCKET_TYPE_CROSS";
    const reqBody: Record<string, unknown> = {
      address: walletAddress,
      bucketType: bucketTypeEnum,
      collateralAssetIndex: params.collateralAssetIndex,
      initialMargin,
      timestamp,
    };
    if (params.bucketId != null && params.bucketId !== "") {
      reqBody.bucketId = params.bucketId;
    }

    const response = await this.httpClient.post("/bucket/v1/bucket", reqBody);
    return response.body;
  }

  /**
   * Withdraw margin from bucket to bank (bucket → bank).
   * Aligned with router/docs/improvements/create_bucket_vs_withdraw_signable_vs_http_body.md:
   * - Signable payload (EIP-712): action, owner, bucketId, assetIndex, amount, nonce, deadline (standards).
   * - HTTP body for POST /bank/v1/withdraw-from-bucket: proto-only, camelCase (MsgWithdrawFromBucketRequest);
   *   no nonce, deadline, signature, chain_id in body to avoid protojson "unknown field" (HTTP 400).
   */
  async withdrawFromBucket(
    params: WithdrawFromBucketParams,
  ): Promise<WithdrawFromBucketResponse> {
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    const assetIndex =
      params.assetIndex ??
      (params.assetId?.toUpperCase() === "USDC" ? 1 : undefined);
    if (assetIndex == null || assetIndex < 1) {
      throw new Error(
        "Provide assetIndex (e.g. 1 for USDC) or assetId 'USDC'",
      );
    }
    if (
      params.amount === undefined ||
      (typeof params.amount === "number" && params.amount <= 0)
    ) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const amountStr = toSatoshi(params.amount);

    // Signable per standards (bank::withdraw_from_bucket): action, owner, bucketId, assetIndex, amount, nonce, deadline.
    const payload: Record<string, any> = {
      action: OpBankWithdrawFromBucket,
      bucketId: params.bucketId,
      assetIndex: Number(assetIndex),
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    // Signature not sent in direct POST body; router unmarshals proto-only. Signing kept for future ingress/wrapper use.

    // Proto-only body (camelCase): address, bucketId, assetIndex, amount, timestamp [, shardId ]
    const timestamp = toRFC3339Nano();
    const reqBody: Record<string, unknown> = {
      address: walletAddress,
      bucketId: params.bucketId,
      assetIndex: Number(assetIndex),
      amount: amountStr,
      timestamp,
    };

    const response = await this.httpClient.post(
      "/bank/v1/withdraw-from-bucket",
      reqBody,
    );
    return response.body;
  }

  /**
   * Mint new tokens
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   *
   * @param params - Bank mint parameters
   * @returns API response
   */
  async bankMint(params: BankMintParams): Promise<BankMintResponse> {
    // Input validation
    if (!params.assetId || params.assetId.trim() === "") {
      throw new Error("assetId cannot be empty");
    }
    if (
      params.amount === undefined ||
      (typeof params.amount === "number" && params.amount <= 0)
    ) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Convert amount from decimal number to satoshi string
    const amountStr = toSatoshi(params.amount);

    // Build payload following bank::mint EIP712 types
    // Payload uses: owner, address, asset, amount, nonce, deadline
    // address is required for signature verification
    const payload: Record<string, any> = {
      action: OpBankMint,
      address: this.getWalletAddress(), // Required for signature verification
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Direct address-to-address asset transfer
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   *
   * @param params - Bank transfer parameters
   * @returns API response
   */
  async bankTransfer(
    params: BankTransferParams,
  ): Promise<BankTransferResponse> {
    // Input validation
    if (!params.to || params.to.trim() === "") {
      throw new Error("to address cannot be empty");
    }
    if (!params.assetId || params.assetId.trim() === "") {
      throw new Error("assetId cannot be empty");
    }
    // Validate amount (already number, no conversion needed)
    if (!params.amount || params.amount <= 0) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Convert amount from decimal number to satoshi string
    const amountStr = toSatoshi(params.amount);

    // Build payload following bank::transfer EIP712 types
    // Payload uses: owner, address, to, asset, amount, nonce, deadline
    // address is required for signature verification
    const payload: Record<string, any> = {
      action: OpBankTransfer,
      address: this.getWalletAddress(), // Required for signature verification
      to: params.to,
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Check position risk before opening/modifying
   * POST /bucket/v1/position/check-risk
   * Important for risk management before placing orders
   *
   * @param params - Position risk check parameters
   * @returns Risk assessment result
   */
  async checkPositionRisk(params: {
    address: string;
    market_index: string;
    side: string;
    size: string;
    leverage: string;
  }): Promise<{
    risk_level: string;
    liquidation_price: string;
    margin_required: string;
    can_open: boolean;
    warnings: string[];
  }> {
    // Input validation
    if (!params.address || params.address.trim() === "") {
      throw new Error("address cannot be empty");
    }
    if (!params.market_index || params.market_index.trim() === "") {
      throw new Error("market_index cannot be empty");
    }
    if (!params.side || (params.side.toUpperCase() !== "LONG" && params.side.toUpperCase() !== "SHORT")) {
      throw new Error("side must be LONG or SHORT");
    }
    if (!params.size || params.size.trim() === "") {
      throw new Error("size cannot be empty");
    }
    if (!params.leverage || params.leverage.trim() === "") {
      throw new Error("leverage cannot be empty");
    }

    const response = await this.httpClient.post(
      "/bucket/v1/position/check-risk",
      {
        address: params.address,
        market_index: params.market_index,
        side: params.side.toUpperCase(),
        size: params.size,
        leverage: params.leverage,
      },
    );

    return response.body;
  }
}
